import { useState, useEffect, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mountain, Download, ArrowLeft, ClipboardList, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { AdminStats } from "@/components/admin/AdminStats";
import { ConversionAnalytics } from "@/components/admin/ConversionAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function renderCell(col, row, onClickWhyFit) {
  const value = row[col.key];
  if ((col.key === "email" || col.key === "parent_email") && value) {
    return (
      <a
        href={`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(value)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#FF3B30] hover:underline"
        data-testid={`email-link-${row.id || row.rider_name}`}
      >
        {value}
      </a>
    );
  }
  if (col.key === "created_at") {
    return formatDate(value);
  }
  if (col.key === "why_good_fit" && value) {
    return (
      <button
        onClick={() => onClickWhyFit(row)}
        className="text-left text-[#A3A3A3] hover:text-[#FF3B30] transition-colors underline decoration-[#262626] hover:decoration-[#FF3B30] cursor-pointer truncate block max-w-[200px]"
        data-testid={`why-fit-btn-${row.id}`}
      >
        {value}
      </button>
    );
  }
  return value || "—";
}

function SignupTable({ data, columns, onClickWhyFit }) {
  if (!data.length) {
    return (
      <div className="text-center py-12 text-[#555] font-body">
        No entries yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-[#262626] hover:bg-transparent">
            {columns.map((col) => (
              <TableHead key={col.key} className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] font-body whitespace-nowrap">
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row) => (
            <TableRow key={row.id} className="border-[#262626] hover:bg-[#1a1a1a]">
              {columns.map((col) => (
                <TableCell key={col.key} className="text-sm text-white font-body max-w-[200px] truncate">
                  {renderCell(col, row, onClickWhyFit)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const studentCols = [
  { key: "full_name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "age", label: "Age" },
  { key: "nationality", label: "Nationality" },
  { key: "discipline", label: "Discipline" },
  { key: "experience_level", label: "Level" },
  { key: "why_good_fit", label: "Why Good Fit" },
  { key: "created_at", label: "Date" },
];

const coachCols = [
  { key: "full_name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "specialty", label: "Specialty" },
  { key: "experience_years", label: "Exp. Years" },
  { key: "why_good_fit", label: "Why Good Fit" },
  { key: "created_at", label: "Date" },
];

const investorCols = [
  { key: "full_name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "company", label: "Company" },
  { key: "investment_interest", label: "Interest" },
  { key: "message", label: "Message" },
  { key: "created_at", label: "Date" },
];

const surveyCols = [
  { key: "rider_name", label: "Rider" },
  { key: "parent_email", label: "Email" },
  { key: "age_dob", label: "Age" },
  { key: "country", label: "Country" },
  { key: "discipline", label: "Discipline" },
  { key: "level", label: "Level" },
  { key: "programme_interest", label: "Programme" },
  { key: "budget_fit", label: "Budget Fit" },
  { key: "readiness", label: "Readiness" },
  { key: "created_at", label: "Date" },
];

export default function AdminPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("student");
  const [data, setData] = useState({ student: [], coach: [], investor: [] });
  const [stats, setStats] = useState({ students: 0, coaches: 0, investors: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [surveyData, setSurveyData] = useState([]);
  const [sendingSurvey, setSendingSurvey] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);

  const fetchData = useCallback(async (type) => {
    try {
      const res = await axios.get(`${API}/admin/signups?signup_type=${type}`);
      setData((prev) => ({ ...prev, [type]: res.data }));
    } catch (err) {
      console.error("Failed to load data:", err);
      toast.error("Failed to load data");
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to load stats:", err);
      toast.error("Failed to load stats");
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics`);
      setAnalytics(res.data);
    } catch (err) {
      console.error("Failed to load analytics:", err);
      toast.error("Failed to load analytics");
    }
  }, []);

  const fetchSurveyData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/survey-responses`);
      setSurveyData(res.data);
    } catch (err) {
      console.error("Failed to load survey data:", err);
    }
  }, []);

  const sendSurveyEmails = async () => {
    setSendingSurvey(true);
    try {
      const res = await axios.post(`${API}/admin/send-survey-emails`);
      toast.success(res.data.message || "Survey emails sent!");
    } catch (err) {
      console.error("Failed to send survey emails:", err);
      toast.error("Failed to send survey emails");
    }
    setSendingSurvey(false);
  };

  useEffect(() => {
    fetchStats();
    fetchAnalytics();
    fetchSurveyData();
    fetchData("student");
    fetchData("coach");
    fetchData("investor");
  }, [fetchData, fetchStats, fetchAnalytics, fetchSurveyData]);

  const exportCSV = (type) => {
    if (type === "survey") {
      window.open(`${API}/admin/survey-export/csv`, "_blank");
    } else {
      window.open(`${API}/admin/export/csv?signup_type=${type}`, "_blank");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-page">
      <div className="border-b border-[#262626] bg-black/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="admin-back-btn"
              onClick={() => navigate("/")}
              className="text-[#A3A3A3] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Mountain className="w-6 h-6 text-[#FF3B30]" />
            <span className="font-heading text-lg tracking-[0.15em]">LNMA ADMIN</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <AdminStats stats={stats} />
        <ConversionAnalytics analytics={analytics} />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <TabsList className="bg-[#111111] border border-[#262626] rounded-none h-auto p-0">
              <TabsTrigger value="student" data-testid="admin-tab-student" className="rounded-none px-6 py-3 text-[#A3A3A3] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-heading uppercase tracking-wider text-xs">
                Students
              </TabsTrigger>
              <TabsTrigger value="coach" data-testid="admin-tab-coach" className="rounded-none px-6 py-3 text-[#A3A3A3] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-heading uppercase tracking-wider text-xs">
                Coaches
              </TabsTrigger>
              <TabsTrigger value="investor" data-testid="admin-tab-investor" className="rounded-none px-6 py-3 text-[#A3A3A3] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-heading uppercase tracking-wider text-xs">
                Investors
              </TabsTrigger>
              <TabsTrigger value="survey" data-testid="admin-tab-survey" className="rounded-none px-6 py-3 text-[#A3A3A3] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-heading uppercase tracking-wider text-xs">
                <ClipboardList className="w-3 h-3 mr-1.5" />
                Surveys ({surveyData.length})
              </TabsTrigger>
            </TabsList>

            <div className="flex gap-3 flex-wrap">
              {activeTab === "survey" ? (
                <>
                  <Button
                    data-testid="send-survey-btn"
                    onClick={sendSurveyEmails}
                    disabled={sendingSurvey}
                    className="bg-[#32D74B] hover:bg-[#28a83d] text-white rounded-none font-heading uppercase tracking-wider text-xs"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingSurvey ? "Sending..." : "Send Survey Emails"}
                  </Button>
                  <Button
                    data-testid="export-survey-csv-btn"
                    onClick={() => exportCSV("survey")}
                    className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white rounded-none font-heading uppercase tracking-wider text-xs"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </>
              ) : (
                <Button
                  data-testid="export-csv-btn"
                  onClick={() => exportCSV(activeTab)}
                  className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white rounded-none font-heading uppercase tracking-wider text-xs"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              )}
            </div>
          </div>

          <div className="bg-[#111111] border border-[#262626]">
            <TabsContent value="student" className="m-0">
              <SignupTable data={data.student} columns={studentCols} onClickWhyFit={setSelectedResponse} />
            </TabsContent>
            <TabsContent value="coach" className="m-0">
              <SignupTable data={data.coach} columns={coachCols} onClickWhyFit={setSelectedResponse} />
            </TabsContent>
            <TabsContent value="investor" className="m-0">
              <SignupTable data={data.investor} columns={investorCols} onClickWhyFit={setSelectedResponse} />
            </TabsContent>
            <TabsContent value="survey" className="m-0">
              <SignupTable data={surveyData} columns={surveyCols} onClickWhyFit={setSelectedResponse} />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      <Dialog open={!!selectedResponse} onOpenChange={(open) => !open && setSelectedResponse(null)}>
        <DialogContent className="bg-[#111111] border-[#262626] text-white max-w-lg rounded-none sm:rounded-none" data-testid="why-fit-dialog">
          {selectedResponse && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase tracking-wider text-white">
                  {selectedResponse.full_name}
                </DialogTitle>
                <DialogDescription className="text-xs text-[#A3A3A3] font-body">
                  {selectedResponse.email} — {formatDate(selectedResponse.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <h4 className="font-heading text-xs uppercase tracking-[0.15em] text-[#FF3B30] mb-3">
                  Why they think they&apos;re a good fit
                </h4>
                <p className="text-sm text-[#A3A3A3] font-body leading-relaxed whitespace-pre-wrap">
                  {selectedResponse.why_good_fit || selectedResponse.message || "—"}
                </p>
              </div>
              {selectedResponse.email && (
                <a
                  href={`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(selectedResponse.email)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full mt-4 bg-[#FF3B30] text-white font-heading uppercase tracking-widest px-8 py-3 text-sm text-center hover:bg-[#FF5C53] transition-all"
                  data-testid="why-fit-email-btn"
                >
                  Email {selectedResponse.full_name}
                </a>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
